import {
  Box,
  IconButton,
  Popover,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import dispatchPortal from "assets/images/whatsnew/dispatch-portal.webp";
import meetDeskdayAi from "assets/images/whatsnew/meet-deskday-ai.webp";
import stripe from "assets/images/whatsnew/stripe.webp";
import { Button, CarosalButton, CloseIcon, WhatsNewIcon2 } from "components";
import {
  useGlobalStore,
  useNonPersistantMainStore,
  useSetFeatureFlagAsSeen,
} from "controller";
import { BottomMenuItem } from "layouts/MainLayout";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { shouldHideWhatsNewForNewUser } from "utils/whatsNewHelper";

export const WhatsNewButton = ({ open }: { open: boolean }) => {
  const ref = useRef<HTMLDivElement>(null);
  const featureFlags = useNonPersistantMainStore((state) => state.featureFlags);
  const whatsNewFeatureFlagCreatedAt = useNonPersistantMainStore(
    (state) => state.whatsNewFeatureFlagCreatedAt,
  );
  const mspCreatedAt = useGlobalStore((state) => state.mspCreatedAt);
  const { mutate: setFeatureAsSeen } = useSetFeatureFlagAsSeen();

  const shouldHide = shouldHideWhatsNewForNewUser(
    mspCreatedAt,
    whatsNewFeatureFlagCreatedAt,
  );

  // Auto-set is_seen flag for new users
  useEffect(() => {
    if (shouldHide && featureFlags?.whats_new) {
      setFeatureAsSeen("whats_new");
    }
  }, [shouldHide, featureFlags, setFeatureAsSeen]);

  // TODO: Check other permissions for other banners
  // const havePermissionForRoles = usePermissionStore((state) =>
  //   state.hasPermissions([PERMISSIONS.CC.CC_RESOURCE_ROLE_RW], "OR")
  // );
  // TODO: change this based on banner
  // const MODAL_STORAGE_KEY = "whatsnewbanner_level";
  // const hideOfferModal = localStorage.getItem(MODAL_STORAGE_KEY);
  const [isModalOpen, setIsModalOpen] = useState(
    false,
    // featureFlags.whats_new && hideOfferModal !== "true" ? true : false
  );
  // const navigate = useNavigate();
  //   useEffect(() => {
  //     const hideOfferModal = localStorage.getItem(MODAL_STORAGE_KEY);
  //     if (featureFlags.whats_new && hideOfferModal !== "true") {
  //       setIsModalOpen(true);
  //     } else {
  //       setIsModalOpen(false);
  //     }
  //   }, [featureFlags]);

  return (
    <>
      {/* TODO: Check other permissions for other banners */}
      {featureFlags.whats_new && !shouldHide && (
        <>
          <Box ref={ref} sx={{ position: "relative" }}>
            <BottomMenuItem
              open={open}
              isActive={false}
              icon={<WhatsNewIcon2 />}
              title="What's New!"
              onClick={() => {
                setIsModalOpen(true);
              }}
              styleSx={{
                background: !open
                  ? "transparent"
                  : "linear-gradient(280.33deg, #ED25C1 5.8%, #FF96E2 35.01%, #A74FFF 63.86%, #874FFF 77.29%, #4D25ED 98.55%)",
              }}
            />
            {!open && (
              <Typography
                variant="caption-3"
                color={"#FFF"}
                sx={{
                  position: "absolute",

                  top: 0,
                  right: 8,
                  fontWeight: "600",
                  transform: "translate(2px,-2px)",
                  background:
                    "linear-gradient(135deg, #ED25C1 -8.98%, #DA64ED 47.33%, #A74FFF 66.54%, #874FFF 82.24%, #8590ff 90.70%)",
                  WebkitTextFillColor: "transparent",
                  WebkitBackgroundClip: "text",
                }}
              >
                New
              </Typography>
            )}
          </Box>
          {isModalOpen && (
            <WhatsNewPopOver
              anchorEl={ref.current}
              onClose={() => setIsModalOpen(false)}
            />
          )}
        </>
      )}
    </>
  );
};

const WhatsNewPopOver = ({
  anchorEl,
  onClose,
}: {
  anchorEl: HTMLElement | null;
  onClose: () => void;
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);

  const WhatsNewContent = [
    {
      image: dispatchPortal,
      title: "Calendar & dispatching",
      description:
        "Plan your team’s day and assign tickets by drag and drop, all on one unified calendar view.",
      route: "dispatch-portal",
      buttonText: "Try it now",
    },
    {
      image: meetDeskdayAi,
      title: "Meet DeskDay AI",
      description:
        "The Triage & Dispatch agent sorts and assigns tickets, Helena Copilot works alongside you on every ticket, and Ask Helena answers your questions instantly.",
      route: "",
      buttonText: "",
    },
    {
      image: stripe,
      title: "Simplify Billing with Stripe",
      description:
        "Connect Stripe with DeskDay to streamline payments and make managing customer billing easier.",
      // `o=stripe` scrolls the Integrations screen to the Stripe card, whose own
      // Connect button runs the existing flow.
      route: "control-center/integrations?o=stripe",
      buttonText: "Connect Stripe",
    },
  ];

  const onTryNowClick = () => {
    navigate(WhatsNewContent[currentIndex].route);
    onClose();
  };

  return (
    <Popover
      open={Boolean(anchorEl)}
      onClose={onClose}
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "bottom", horizontal: "left" }}
      PaperProps={{
        sx: {
          //  background:
          //      "linear-gradient(183.64deg, #ED25C1 -40.77%, #F096FF 5.21%, #ECB4E0 13.05%, #FF96E2 23.25%, #FAF8FA 41.48%, #A74FFF 64.04%, #874FFF 76.34%, #4D25ED 97.19%)",
          ml: "24px",
          borderRadius: "8px",
          transform: "translate3d(0,0,-1px)",
        },
      }}
    >
      <Stack
        bgcolor={theme.palette.neutral.main}
        direction="column"
        overflow="hidden"
        sx={{
          gap: "8px",
          width: "400px",
          height: "min(90vh, 540px)",
          display: "flex",
          alignItems: "center",
          margin: "auto",
          position: "relative",
          boxSizing: "border-box",
          backgroundClip: "padding-box",
          border: "solid 1px transparent",
          borderRadius: "8px",
          background:
            "linear-gradient(white, white) padding-box, linear-gradient(183.64deg, #ED25C1 -40.77%, #F096FF 5.21%, #ECB4E0 13.05%, #FF96E2 23.25%, #FAF8FA 41.48%, #A74FFF 64.04%, #874FFF 76.34%, #4D25ED 97.19%) border-box",
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            width: "24px",
            height: "24px",
            zIndex: 1,
          }}
        >
          <CloseIcon htmlColor="white" />
        </IconButton>

        <Stack
          direction="row"
          sx={{ width: "100%", height: "280px", position: "relative" }}
        >
          {WhatsNewContent.map((item, index) => (
            <img
              key={index}
              src={item.image}
              alt="whatsnew"
              style={{
                height: "100%",
                objectFit: "cover",
                transition: "all 0.3s ease-in-out",
                width: currentIndex === index ? "400px" : "0px",
              }}
            />
          ))}
        </Stack>

        <Stack sx={{ width: "100%", flex: 1, p: "16px 24px", gap: "16px" }}>
          <CarosalButton
            currentIndex={currentIndex}
            setCurrentIndex={setCurrentIndex}
            totalItems={WhatsNewContent.length}
          />
          <Stack
            direction={"column"}
            sx={{
              width: "100%",
              flex: "1",
              position: "relative",
              overflow: "hidden",
              gap: "16px",
            }}
          >
            <Typography width={"100%"}>
              {WhatsNewContent[currentIndex].title}
            </Typography>
            <Typography variant="body-small" width={"100%"}>
              {WhatsNewContent[currentIndex].description}
            </Typography>
          </Stack>
          {WhatsNewContent[currentIndex].buttonText && (
            <Button fullWidth onClick={onTryNowClick}>
              {WhatsNewContent[currentIndex].buttonText}
            </Button>
          )}
        </Stack>
      </Stack>
    </Popover>
  );
};
